import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import api from '@/lib/api';

/* „Angebot erstellen“ aus der Kundenakte (28.10.2026, Brief 7/8: Vertrieb erstellt Angebote manuell). Dieselben Karten wie
   der Check-up-Stepper (GET /api/kunden/{id}/angebot-karten, Preise vom Server) — höchstens ein Coaching-Paket, dazu
   wahlweise Ernährungscoaching. Gesendet wird über denselben Weg wie „Angebot senden“ im Check-up
   (POST /api/kunden/{id}/angebot → leistungsangebot.senden): erscheint in der App, gilt `angebot_tage` Tage. */

const euro = (n) => (n === null || n === undefined ? '–' : `${Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

function preisZeile(k) {
  if (k.abrechnung === 'monatlich') return `${euro(k.preis)} je Monat${k.mindestlaufzeit_monate ? `, mind. ${k.mindestlaufzeit_monate} Monate` : ''}`;
  return `${euro(k.preis)} einmalig`;
}

/* `client`/`basis`: auf /coach derselbe Dialog über die Trainer-Sitzung (`/api/coach/kunden/{id}/…`) — Standard CRM. */
export default function AngebotErstellen({ leadId, offen, onClose, onGesendet, client = api, basis = '/kunden' }) {
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [auswahl, setAuswahl] = useState([]);
  const [mail, setMail] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => {
    if (!offen) return;
    setDaten(null); setFehler(null); setAuswahl([]); setMail(false);
    client.get(`${basis}/${leadId}/angebot-karten`).then((r) => setDaten(r.data))
      .catch((e) => setFehler(e.response?.data?.detail || 'Angebote konnten nicht geladen werden'));
  }, [offen, leadId, client, basis]);

  const karten = daten?.karten || [];
  const pakete = karten.filter((k) => k.schluessel !== 'ernaehrung');
  const ern = karten.find((k) => k.schluessel === 'ernaehrung');
  const mitAufbau = auswahl.some((s) => s.startsWith('aufbau_'));
  const paketWaehlen = (s) => setAuswahl((x) => (x.includes(s) ? x.filter((y) => y !== s) : [...x.filter((y) => y === 'ernaehrung'), s]));
  const ernUmschalten = () => setAuswahl((x) => (x.includes('ernaehrung') ? x.filter((y) => y !== 'ernaehrung') : [...x, 'ernaehrung']));

  const senden = async () => {
    setLaeuft(true);
    try {
      const r = await client.post(`${basis}/${leadId}/angebot`, { schluessel: auswahl, kanal_email: mail });
      toast.success(`Angebot gesendet – gilt bis ${String(r.data.gueltig_bis || '').slice(8, 10)}.${String(r.data.gueltig_bis || '').slice(5, 7)}.`);
      onGesendet?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Angebot nicht gesendet');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog open={offen} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="angebot-erstellen">
        <DialogHeader><DialogTitle>Angebot erstellen</DialogTitle></DialogHeader>
        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
        {!daten && !fehler && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> lädt …</div>}
        {daten && (
          <div className="space-y-3">
            {daten.offen?.length > 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-300">Es liegt schon {daten.offen.length === 1 ? 'ein offenes Angebot' : `${daten.offen.length} offene Angebote`} in der App.</p>
            )}
            <div className="grid gap-2">
              {pakete.map((k) => {
                const an = auswahl.includes(k.schluessel);
                return (
                  <button key={k.schluessel} type="button" aria-pressed={an} onClick={() => paketWaehlen(k.schluessel)}
                    className={`rounded-lg border p-3 text-left text-sm ${an ? 'border-marke bg-marke/5' : 'border-border'}`} data-testid={`angebot-karte-${k.schluessel}`}>
                    <span className="block font-semibold">{k.titel}</span>
                    {k.kurz && <span className="block text-xs text-muted-foreground">{k.kurz}</span>}
                    <span className="block tabular-nums">{preisZeile(k)}</span>
                  </button>
                );
              })}
            </div>
            {ern && (
              <label className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={auswahl.includes('ernaehrung')} onChange={ernUmschalten} data-testid="angebot-karte-ernaehrung" />
                  <span className="font-semibold">{ern.titel}</span>
                </span>
                <span className="tabular-nums text-xs">{mitAufbau && daten.gratis_tage_bei_aufbau ? `erster Monat gratis, danach ${preisZeile(ern)}` : preisZeile(ern)}</span>
              </label>
            )}
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} /> zusätzlich per E-Mail</label>
            <p className="text-xs text-muted-foreground">Das Angebot erscheint in der App des Mitglieds und gilt {daten.angebot_tage || 7} Tage. Angenommen wird dort per Unterschrift.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button className="bg-marke hover:bg-marke-dunkel text-white" disabled={!auswahl.length || laeuft} onClick={senden} data-testid="angebot-senden">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Angebot senden'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
